import { ContextualMenu, IContextualMenuItem } from '@fluentui/react';
import AppContext from 'App/AppContext';
import React, { Fragment, useContext } from 'react';
import { JSONTree, KeyPath, LabelRenderer, ShouldExpandNodeInitially } from 'react-json-tree';

import { IPText, IPTextField } from 'baseComponents';
import { useTranslation } from 'react-i18next';
import { IArrayViewerDataProps } from './ArrayViewer';

export interface INodeInfo {
  keyPath: KeyPath;
  nodeType: string;
  isExpanded: boolean;
  isExpandable: boolean;
  isSelected: boolean;
  isIgnored: boolean;
  isExtracted: boolean;
  isArray: boolean;
}

export interface IJSONViewerProps extends IArrayViewerDataProps {
  expandInitially?: boolean;
  hideRoot?: boolean;
  allowLabelSelect?: boolean;
  allowValueSelect?: boolean;
  selectedNode?: KeyPath;
  ignoredNodes?: KeyPath[];
  extractedNode?: KeyPath;
  arrayNode?: KeyPath;
  onUpdateArrayNode?: (item: KeyPath | undefined) => void;
  onUpdateSelectedNode?: (item: KeyPath | undefined) => void;
  onUpdateIgnoredNodes?: (items: KeyPath[]) => void;
  onUpdateExtracedField?: (item: KeyPath | undefined) => void;
}

export enum NodeActions {
  None = 0,
  Select = 1,
  Ignore = 2,
  Extract = 3,
  Array = 4,
}

export const JSONViewer = (props: IJSONViewerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpi']);
  const [showActionMenu, setShowActionMenu] = React.useState<boolean>(false);
  const [currentNode, setCurrentNode] = React.useState<INodeInfo | undefined>(undefined);

  const nodeArrayColor = '#deb17a';
  const nodeSelectedColor = '#c7c94f';
  const nodeExtractedColor = '#25942e';
  const nodeIgnoredColor = '#eb7575';
  const nodeHoverColor = '#7890de';
  const nodeHoverBorderColor = '#1f227a';

  const getEvidenceAsJSON = (raw: unknown): unknown => {
    try {
      if (typeof raw === 'object') return raw;
      if (typeof raw === 'string') return JSON.parse(raw);

      return null;
    } catch {
      return null;
    }
  };

  const getLabelStyles = (isSelected: boolean, isIgnored: boolean, isExtracted: boolean, isArray: boolean) => {
    if (props.allowLabelSelect) {
      if (isArray) {
        return {
          root: {
            padding: '2px',
            margin: '2px',
            borderRadius: '3px',
            border: '1px solid transparent',
            background: nodeArrayColor,
            cursor: 'pointer',
            '&:hover': {
              border: `1px solid ${nodeHoverBorderColor}`,
              borderRadius: '3px',
              cursor: 'pointer',
            },
          },
        };
      } else if (isSelected) {
        return {
          root: {
            padding: '2px',
            margin: '2px',
            borderRadius: '3px',
            border: '1px solid transparent',
            background: nodeSelectedColor,
            cursor: 'pointer',
            '&:hover': {
              border: `1px solid ${nodeHoverBorderColor}`,
              borderRadius: '3px',
              cursor: 'pointer',
            },
          },
        };
      } else if (isIgnored) {
        return {
          root: {
            padding: '2px',
            margin: '2px',
            borderRadius: '3px',
            border: '1px solid transparent',
            background: nodeIgnoredColor,
            cursor: 'pointer',
            '&:hover': {
              border: `1px solid ${nodeHoverBorderColor}`,
              borderRadius: '3px',
              cursor: 'pointer',
            },
          },
        };
      } else if (isExtracted) {
        return {
          root: {
            padding: '2px',
            margin: '2px',
            borderRadius: '3px',
            border: '1px solid transparent',
            background: nodeExtractedColor,
            cursor: 'pointer',
            '&:hover': {
              border: `1px solid ${nodeHoverBorderColor}`,
              borderRadius: '3px',
              cursor: 'pointer',
            },
          },
        };
      } else {
        return {
          root: {
            padding: '2px',
            margin: '2px',
            borderRadius: '3px',
            border: '1px solid transparent',
            '&:hover': {
              border: `1px solid ${nodeHoverBorderColor}`,
              borderRadius: '3px',
              background: nodeHoverColor,
              cursor: 'pointer',
            },
          },
        };
      }
    } else {
      return undefined;
    }
  };

  const partOfKeyPathCol = (col: KeyPath[] | undefined, val: KeyPath | undefined): boolean => {
    if (!col) return false;

    return col.some((c) => partOfKeyPath(c, val));
  };

  const partOfKeyPath = (k: KeyPath | undefined, part: KeyPath | undefined): boolean => {
    if ((!k && part) || (!k && part)) return false;
    if (!k && !part) return true;
    if (!k || !part) return false;

    if (part.length > k.length) return false;

    for (let idx = 0; idx < part.length; idx++) {
      if (part[idx] !== k[idx]) {
        return false;
      }
    }

    return true;
  };

  const keyPathEqual = (k1: KeyPath | undefined, k2: KeyPath | undefined): boolean => {
    if ((!k1 && k2) || (!k2 && k1)) return false;
    if (!k1 && !k2) return true;
    if (!k1 || !k2) return false;

    if (k1.length !== k2.length) return false;
    if (k1.length !== k2.length) return false;

    for (let idx = 0; idx < k1.length; idx++) {
      if (k1[idx] !== k2[idx]) {
        return false;
      }
    }

    return true;
  };

  const removeFromIgnored = (val: KeyPath) => {
    const newSelection = props.ignoredNodes?.filter((n) => !keyPathEqual(n, val));
    if (props.onUpdateIgnoredNodes) props.onUpdateIgnoredNodes(newSelection ?? []);
  };

  const removeFromSelected = (val: KeyPath) => {
    if (keyPathEqual(props.selectedNode, val)) {
      if (props.onUpdateSelectedNode) props.onUpdateSelectedNode(undefined);
    }
  };

  const removeFromArray = (val: KeyPath) => {
    if (keyPathEqual(props.arrayNode, val)) {
      if (props.onUpdateArrayNode) props.onUpdateArrayNode(undefined);
    }
  };

  const removeExtractedField = (val: KeyPath) => {
    if (keyPathEqual(props.extractedNode, val)) {
      if (props.onUpdateExtracedField) props.onUpdateExtracedField(undefined);
    }
  };

  const onNodeClick = (
    keyPath: KeyPath,
    nodeType: string,
    isExpanded: boolean,
    isExpandable: boolean,
    isSelected: boolean,
    isIgnored: boolean,
    isExtracted: boolean,
    isArray: boolean,
    action: NodeActions,
  ) => {
    switch (action) {
      case NodeActions.Array:
        if (!isArray) {
          if (props.onUpdateArrayNode) props.onUpdateArrayNode(keyPath);
          removeFromIgnored(keyPath);
          removeExtractedField(keyPath);
          removeFromSelected(keyPath);
        } else {
          removeFromArray(keyPath);
        }
        break;
      case NodeActions.Select:
        if (!isSelected) {
          if (props.onUpdateSelectedNode) props.onUpdateSelectedNode(keyPath);
          removeFromIgnored(keyPath);
          removeExtractedField(keyPath);
        } else {
          removeFromSelected(keyPath);
        }
        break;
      case NodeActions.Ignore:
        if (!isIgnored) {
          const newSelection = [...(props.ignoredNodes ?? [])];
          newSelection.push(keyPath);
          if (props.onUpdateIgnoredNodes) props.onUpdateIgnoredNodes(newSelection);
          removeFromSelected(keyPath);
          removeExtractedField(keyPath);
        } else {
          removeFromIgnored(keyPath);
        }
        break;
      case NodeActions.Extract:
        if (!isExtracted) {
          if (props.onUpdateExtracedField) props.onUpdateExtracedField(keyPath);
          removeFromIgnored(keyPath);
          removeFromSelected(keyPath);
        } else {
          if (props.onUpdateExtracedField) props.onUpdateExtracedField(undefined);
        }
        break;
      default:
        removeFromIgnored(keyPath);
        removeFromSelected(keyPath);
        if (props.onUpdateExtracedField) props.onUpdateExtracedField(undefined);
        break;
    }
  };

  const getNodeActionMenuProps = (): IContextualMenuItem[] => {
    if (!currentNode) return [];

    const items: IContextualMenuItem[] = [
      {
        key: 'array',
        text: t('kpi:TabEvidence.JSONViewer.NodeActions.Array'),
        disabled: currentNode.nodeType !== 'Array',
        canCheck: true,
        checked: currentNode.isArray,
        onClick: () => {
          onNodeClick(
            currentNode?.keyPath,
            currentNode?.nodeType,
            currentNode?.isExpanded,
            currentNode?.isExpandable,
            currentNode?.isSelected,
            currentNode?.isIgnored,
            currentNode?.isExtracted,
            currentNode?.isArray,
            NodeActions.Array,
          );
        },
      },
      {
        key: 'select',
        text: t('kpi:TabEvidence.JSONViewer.NodeActions.Select'),
        canCheck: true,
        checked: currentNode.isSelected,
        onClick: () => {
          onNodeClick(
            currentNode?.keyPath,
            currentNode?.nodeType,
            currentNode?.isExpanded,
            currentNode?.isExpandable,
            currentNode?.isSelected,
            currentNode?.isIgnored,
            currentNode?.isExtracted,
            currentNode?.isArray,
            NodeActions.Select,
          );
        },
      },
      {
        key: 'ignore',
        text: t('kpi:TabEvidence.JSONViewer.NodeActions.Ignore'),
        canCheck: true,
        checked: currentNode.isIgnored,
        onClick: () => {
          onNodeClick(
            currentNode?.keyPath,
            currentNode?.nodeType,
            currentNode?.isExpanded,
            currentNode?.isExpandable,
            currentNode?.isSelected,
            currentNode?.isIgnored,
            currentNode?.isExtracted,
            currentNode?.isArray,
            NodeActions.Ignore,
          );
        },
      },
      {
        key: 'extract',
        text: t('kpi:TabEvidence.JSONViewer.NodeActions.Extract'),
        canCheck: true,
        checked: currentNode.isExtracted,
        onClick: () => {
          onNodeClick(
            currentNode?.keyPath,
            currentNode?.nodeType,
            currentNode?.isExpanded,
            currentNode?.isExpandable,
            currentNode?.isSelected,
            currentNode?.isIgnored,
            currentNode?.isExtracted,
            currentNode?.isArray,
            NodeActions.Extract,
          );
        },
      },
    ];

    return items;
  };

  const getNodeInfo = (
    keyPath: KeyPath,
    nodeType: string,
    isExpanded: boolean,
    isExpandable: boolean,
    isSelected: boolean,
    isIgnored: boolean,
    isExtracted: boolean,
    isArray: boolean,
  ): INodeInfo => {
    return {
      keyPath,
      nodeType,
      isExpanded: isExpanded,
      isExpandable: isExpandable,
      isSelected: isSelected,
      isIgnored: isIgnored,
      isExtracted: isExtracted,
      isArray: isArray,
    };
  };

  const renderLabels: LabelRenderer = (
    keyPath: KeyPath,
    nodeType: string,
    expanded: boolean,
    expandable: boolean,
  ): React.ReactNode => {
    const isSelected = keyPathEqual(props.selectedNode, keyPath);
    const isIgnored = partOfKeyPathCol(props.ignoredNodes, keyPath);
    const isExtracted = keyPathEqual(props.extractedNode, keyPath);
    const isArray = keyPathEqual(props.arrayNode, keyPath);
    const nodeInfo = getNodeInfo(keyPath, nodeType, expanded, expandable, isSelected, isIgnored, isExtracted, isArray);

    return (
      <IPText
        id={'action-menu-target-' + getNodeId(nodeInfo)}
        key={keyPath.toString()}
        onClick={(ev) => {
          ev.stopPropagation();
          if (props.allowLabelSelect) {
            setCurrentNode(nodeInfo);
            setShowActionMenu(true);
          }
        }}
        styles={getLabelStyles(isSelected, isIgnored, isExtracted, isArray)}
      >{`${keyPath[0]}:`}</IPText>
    );
  };

  const expandNodes: ShouldExpandNodeInitially = () => {
    return props.expandInitially ?? true;
  };

  const getNodeId = (node: INodeInfo): string => {
    let id: string = '';

    for (let idx = 0; idx < node.keyPath.length; idx++) {
      id += node.keyPath[idx].toString();
    }

    return id;
  };

  const theme = {
    base00: '#2e2e2e',
    base01: '#383830',
    base02: '#49483e',
    base03: '#75715e',
    base04: '#a59f85',
    base05: '#f8f8f2',
    base06: '#f5f4f1',
    base07: '#f9f8f5',
    base08: '#f92672',
    base09: '#fd971f',
    base0A: '#f4bf75',
    base0B: '#a6e22e',
    base0C: '#a1efe4',
    base0D: '#66d9ef',
    base0E: '#ae81ff',
    base0F: '#cc6633',
  };

  //
  // Main render
  //
  const evidence = getEvidenceAsJSON(props.data);

  if (!evidence && typeof props.data === 'string') {
    return <IPTextField value={props.data} multiline autoAdjustHeight />;
  }

  return (
    <Fragment>
      <JSONTree
        hideRoot={props.hideRoot ?? true}
        theme={theme}
        invertTheme={!appContext.useDarkMode}
        data={evidence}
        shouldExpandNodeInitially={expandNodes}
        labelRenderer={renderLabels}
      />
      {showActionMenu && currentNode && (
        <ContextualMenu
          items={getNodeActionMenuProps()}
          hidden={false}
          target={'#action-menu-target-' + getNodeId(currentNode)}
          onDismiss={() => setShowActionMenu(false)}
        />
      )}
    </Fragment>
  );
};
